import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type } from 'arktype'
import { Loader2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/categories/')({
  component: RouteComponent,
})

// Schema for creating/editing day type categories
const dayTypeCategoryFormSchema = type({
  name: 'string',
})

type DayTypeCategoryFormValues = typeof dayTypeCategoryFormSchema.infer

function AddDayTypeDialog() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const form = useForm<DayTypeCategoryFormValues>({
    resolver: arktypeResolver(dayTypeCategoryFormSchema),
    defaultValues: {
      name: '',
    },
  })

  const { mutate: createDayTypeCategory } = useMutation({
    ...api.calendar.createDayTypeCategory.mutationOptions(),
  })

  async function onSubmit(data: DayTypeCategoryFormValues) {
    createDayTypeCategory(
      {
        name: data.name,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['dayTypes'] })
          toast.success('Day type created successfully')
        },
        onError: (error) => {
          toast.error('Failed to create day type')
          console.error(error)
        },
      },
    )
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button disabled={true}>Add New Day Type</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Day Type</DialogTitle>
          <DialogDescription>
            Create a new day type category. Click save when you&apos;re done.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter day type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit">Create</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

function RouteComponent() {
  const api = useTRPC()
  const {
    data: dayTypeCategories,
    isPending,
    error,
  } = useQuery({
    ...api.calendar.getDayTypeCategories.queryOptions(),
  })

  if (isPending) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-[200px] w-full items-center justify-center">
        <p className="text-destructive">Failed to load day types</p>
      </div>
    )
  }

  return (
    <DashboardShell>
      <DashboardHeader heading="Day Type Categories" text="Manage categories" />
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Day Types</h1>
          <AddDayTypeDialog />
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                {/* <TableHead>Wage Multiplier</TableHead> */}
                <TableHead>Created At</TableHead>
                <TableHead>Updated At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dayTypeCategories.map((dayType) => (
                <TableRow key={dayType.calendarCategoryId}>
                  <TableCell>{dayType.name}</TableCell>
                  <TableCell>{DateTime.fromJSDate(dayType.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{DateTime.fromJSDate(dayType.updatedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardShell>
  )
}
